import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AxiosProgressEvent } from 'axios';
import type { PageConfig } from 'c-config';
import { PostAuthRoutes } from 'c-routes';
import { logFileImportIcon } from 'c-main/icons';
import type { PopDisplays_LogFileImport } from 'c-sdk';
import { PermissionName } from 'c-sdk';
import {
    FilterableEntityTable,
    FilterableEntityTableRendererType,
    generateColumn,
} from 'c-pagination';
import { FullPageEntityTableWrapper } from 'c-wrapper';
import { LogFileImportFileNameCell } from 'c-pop-displays/Components/LogFileImportTableCells';
import ReactGA from 'react-ga';
import type { LinearProgressProps } from '@mui/material';
import {
    Box,
    Button,
    CircularProgress,
    IconButton,
    LinearProgress,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import LaunchIcon from '@mui/icons-material/Launch';

import {
    Alert,
    AllEntitiesDropdown,
    Checkbox,
    DialogV2,
    FileUploadDropzone,
    SelectField,
} from 'c-components';
import { useBoolean } from 'react-hanger';
import { useAPIClientRequest } from 'c-hooks';
import { apiClient, useEntityData, usePaginatedEntityData } from 'c-data';
import { useCommonTranslation } from 'c-translation';
import { NetworkRequestState } from 'c-data-layer';
import { CheckCircle, Error } from '@mui/icons-material';
import DoneIcon from '@mui/icons-material/Done';
import type { Theme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import {
    canApplyBulkAction,
    pageSelectionState,
    togglePageSelection,
} from 'c-pop-displays/Lib/pageSelection';

const paginationTag = 'log-file-import-list-search';
const textSearchCols = ['file'];

const columns = (setIds, ids: number[], paginatedIds: number[], setErrorPopUp) => [
    generateColumn({
        headerTitle: '',
        field: 'selectField',
        entityName: 'PopDisplays_LogFileImport',
        orderable: true,
        RenderHeader: () => {
            const { allSelected, someSelected } =
                Array.isArray(ids) && Array.isArray(paginatedIds)
                    ? pageSelectionState(ids, paginatedIds)
                    : { allSelected: false, someSelected: false };

            const toggleAll = () => {
                setIds(prev => togglePageSelection(prev, paginatedIds, allSelected));
            };
            return (
                <Stack direction="row">
                    <Box onClick={e => e.stopPropagation()}>
                        <Checkbox
                            checked={allSelected}
                            onChange={toggleAll}
                            indeterminate={!allSelected && someSelected}
                        />
                    </Box>
                </Stack>
            );
        },
        RenderCell: ({ entity }) => {
            const isChecked = ids.includes(entity.id);
            const toggleSingle = e => {
                e.stopPropagation();
                e.preventDefault();
                if (e.target.checked) {
                    setIds(prev => [...prev, entity.id]);
                } else {
                    setIds(prev => prev.filter(id => id !== entity.id));
                }
            };
            return (
                <Stack direction="row">
                    <Box onClick={e => e.stopPropagation()}>
                        <Checkbox checked={isChecked} onChange={toggleSingle} />
                    </Box>
                </Stack>
            );
        },
    }),
    generateColumn<PopDisplays_LogFileImport>({
        headerTitle: 'Modules.PopDisplays.LogFileImport.table.idCol',
        field: 'id',
        entityName: 'PopDisplays_LogFileImport',
        orderable: true,
    }),
    generateColumn<PopDisplays_LogFileImport>({
        headerTitle: 'Modules.PopDisplays.LogFileImport.table.typeCol',
        field: 'type',
        entityName: 'PopDisplays_LogFileImport',
        orderable: true,
    }),
    generateColumn<PopDisplays_LogFileImport>({
        headerTitle: 'Modules.PopDisplays.LogFileImport.table.statusCol',
        field: 'status',
        entityName: 'PopDisplays_LogFileImport',
        orderable: true,
        RenderCell: ({ entity }) => (
            <Stack direction="row" gap={1} display="flex" alignItems="center">
                {entity.status}
                {entity.error !== '' && (
                    <IconButton onClick={() => setErrorPopUp(entity.error)}>
                        <LaunchIcon />
                    </IconButton>
                )}
            </Stack>
        ),
    }),
    generateColumn<PopDisplays_LogFileImport>({
        headerTitle: 'Modules.PopDisplays.LogFileImport.table.processing',
        field: 'processing',
        entityName: 'PopDisplays_LogFileImport',
        orderable: true,
        RenderCell: ({ entity }) => {
            if (entity.processing) {
                return <CircularProgress size={25} />;
            }
            return <DoneIcon />;
        },
    }),
    generateColumn<PopDisplays_LogFileImport>({
        headerTitle: 'Modules.PopDisplays.LogFileImport.table.dateCol',
        field: 'datetime',
        entityName: 'PopDisplays_LogFileImport',
        dataType: 'date',
        dateProps: { showTime: true },
        orderable: true,
    }),
    generateColumn<PopDisplays_LogFileImport>({
        headerTitle: 'Modules.PopDisplays.LogFileImport.table.fileCol',
        field: 'file',
        entityName: 'PopDisplays_LogFileImport',
        RenderCell: LogFileImportFileNameCell,
        orderable: true,
    }),
    generateColumn<PopDisplays_LogFileImport>({
        headerTitle: 'Modules.PopDisplays.LogFileImport.table.fileSizeCol',
        field: 'filesize',
        entityName: 'PopDisplays_LogFileImport',
        dataType: 'fileSize',
    }),
    generateColumn<PopDisplays_LogFileImport>({
        headerTitle: 'Modules.PopDisplays.LogFileImport.table.createdCol',
        field: 'created_at',
        entityName: 'PopDisplays_LogFileImport',
        dataType: 'date',
        dateProps: { showTime: true },
        orderable: true,
    }),
    generateColumn<PopDisplays_LogFileImport>({
        headerTitle: 'Modules.PopDisplays.LogFileImport.table.updatedCol',
        field: 'updated_at',
        entityName: 'PopDisplays_LogFileImport',
        dataType: 'date',
        dateProps: { showTime: true },
        orderable: true,
    }),
];

const handleDownloadClassic = () => {
    const headers = ['frameid', 'startdate', 'enddate', 'creativename'];
    const rows = [
        [37478, '2026-07-16 00:00:00', '2026-10-04 00:00:00', 'Amgen'],
        [38481, '2026-07-13 00:00:00', '2026-10-04 00:00:00', 'Amgen'],
    ];
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = 'classic-template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

const handleDownloadDateAndHour = () => {
    const headers = [
        'date',
        'time',
        'media_name',
        'channel_name',
        'plays',
        'duration',
        'impacts',
        'value',
        'media_extid',
        'creative_ref',
        'campaign_name',
        'campaign_extid',
        'campaign_ref',
        'buyer',
        'agency',
        'brand',
        'uuid',
        'channel_extid',
        'frame_name',
        'frame_extid',
        'player_name',
        'player_extid',
        'ext_source',
        'custom',
    ];

    const rows = [
        [
            '10/03/2025',
            '22',
            '123.jpg',
            '123',
            '1',
            '6',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
        ],
        [
            '12/03/2025',
            '19',
            '123.jpg',
            '123',
            '18',
            '10',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
        ],
    ];

    const csvContent = [headers, ...rows].map(e => e.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'Date And Time.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

const handleDownloadDateTime = () => {
    const headers = [
        'datetime',
        'media_name',
        'channel_name',
        'media_extid',
        'plays',
        'duration',
        'impacts',
        'value',
        'creative_ref',
        'campaign_name',
        'campaign_extid',
        'campaign_ref',
        'buyer',
        'agency',
        'brand',
        'uuid',
        'channel_extid',
        'frame_name',
        'frame_extid',
        'player_name',
        'player_extid',
        'ext_source',
        'custom',
    ];

    const rows = [
        [
            '02/02/2025 06:00',
            '123.jpg',
            '123',
            '1120810865',
            '1',
            '6',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
        ],
        [
            '02/02/2025 07:00',
            '123.jpg',
            '123',
            '1120810865',
            '18',
            '10',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
        ],
    ];

    const csvContent = [headers, ...rows].map(e => e.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'DateTime.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

const LogFileImportListPage = () => {
    useEffect(() => {
        ReactGA.pageview(window.location.pathname + window.location.search);
    }, []);
    const { paginatedData } = usePaginatedEntityData(paginationTag, 'PopDisplays_LogFileImport');
    const paginatedIds = useMemo(() => paginatedData?.data ?? [], [paginatedData]);
    const [ids, setIds] = useState<number[]>([]);
    const [action, setAction] = useState<'reprocess' | 'delete' | 'ignore' | '' | null>(null);
    const [dropdownValue, setDropdownValue] = useState(null);
    const uploadState = useBoolean(false);
    const classicUploadState = useBoolean(false);
    const [errorPopUp, setErrorPopUp] = useState<string | null>(null);
    const { start, data, error, reset, requestState } = useAPIClientRequest(
        apiClient.PopDisplays.logUpload,
    );
    const {
        start: classicStart,
        data: classicDate,
        reset: classicReset,
        error: classicError,
        requestState: classicRequestState,
    } = useAPIClientRequest(apiClient.PopDisplays.classicLogUpload);
    const {
        start: statusStart,
        isLoading,
        hasSucceeded,
        error: statusError,
        reset: statusReset,
    } = useAPIClientRequest(apiClient.PopDisplays.logChangeStatus);
    const [files, setFiles] = useState([]);
    const [progress, setProgress] = useState<AxiosProgressEvent | null>(null);
    const [classicFiles, setClassicFiles] = useState([]);
    const [classicProgress, setClassicProgress] = useState<AxiosProgressEvent | null>(null);
    const progressPercentage = useMemo(
        () =>
            progress?.total != null && progress?.loaded != null
                ? (progress.loaded / progress.total) * 100
                : 0,
        [progress],
    );
    const classicProgressPercentage = useMemo(
        () =>
            classicProgress?.total != null && classicProgress?.loaded != null
                ? (classicProgress.loaded / classicProgress.total) * 100
                : 0,
        [classicProgress],
    );
    const classicProgressColor = useMemo<LinearProgressProps['color']>(() => {
        if (classicRequestState === NetworkRequestState.Error) return 'error';
        if (classicProgressPercentage >= 100) return 'success';

        return 'primary';
    }, [classicRequestState, classicProgressPercentage]);
    const progressColor = useMemo<LinearProgressProps['color']>(() => {
        if (requestState === NetworkRequestState.Error) return 'error';
        if (progressPercentage >= 100) return 'success';

        return 'primary';
    }, [requestState, progressPercentage]);
    const onFileAdded = useCallback((files: File[]) => {
        setFiles(prev => [...prev, ...files]);
    }, []);
    const onClassicFileAdded = useCallback((files: File[]) => {
        setClassicFiles(prev => [...prev, ...files]);
    }, []);
    const t = useCommonTranslation();
    const uploadFiles = useCallback(async () => {
        reset();
        const formData = new FormData();
        formData.append('source_id', dropdownValue);
        files.forEach(f => formData.append('files[]', f));

        await start(formData, setProgress);
    }, [dropdownValue, files, reset, start]);
    const uploadClassicFiles = useCallback(async () => {
        classicReset();
        const formData = new FormData();
        classicFiles.forEach(f => formData.append('files[]', f));

        await classicStart(formData, setClassicProgress);
    }, [classicFiles, classicReset, classicStart]);
    const closeDialog = useCallback(() => {
        uploadState.setFalse();
        setFiles([]);
        setDropdownValue(null);
        reset();
    }, [reset, uploadState]);
    const closeClassicDialog = useCallback(() => {
        classicUploadState.setFalse();
        setClassicFiles([]);
        classicReset();
    }, [classicReset, classicUploadState]);
    useEffect(() => {
        if (data?.data?.success) {
            setFiles([]);
            setDropdownValue(null);
            setTimeout(() => {
                reset();
            }, 3000);
        }
        if (classicDate?.data?.success) {
            setClassicFiles([]);
            setTimeout(() => {
                classicReset();
            }, 3000);
        }
    }, [classicDate?.data?.success, classicReset, data, reset]);
    const tableRef = useRef<any>(null);
    useEffect(() => {
        if (hasSucceeded) {
            tableRef.current.search(1);
            setIds([]);
            setAction(null);
        }
    }, [hasSucceeded]);

    const rawUploadLogHandler = useMemo(
        () => (
            <>
                <Button onClick={uploadState.setTrue} variant="contained">
                    {t('Modules.PopDisplays.LogFileImport.uploadRawLog')}
                </Button>
                <DialogV2 onClose={closeDialog} open={uploadState.value} maxWidth="xl">
                    <Stack
                        direction="row"
                        gap={2}
                        alignItems="center"
                        justifyContent="center"
                        width="100%"
                    >
                        <Button
                            onClick={handleDownloadDateAndHour}
                            variant="contained"
                            sx={{ minWidth: '10rem' }}
                        >
                            {t('Modules.PopDisplays.LogFileImport.downloadDateAndHourTemplate')}
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleDownloadDateTime}
                            sx={{ minWidth: '10rem' }}
                        >
                            {t('Modules.PopDisplays.LogFileImport.downloadDateTimeTemplate')}
                        </Button>
                    </Stack>

                    <AllEntitiesDropdown
                        entityName="PopDisplays_LogImportSource"
                        labelField="name"
                        onChange={setDropdownValue}
                        value={dropdownValue}
                        multi
                        label={t('Modules.PopDisplays.LogFileImport.selectLogSource')}
                    />
                    <FileUploadDropzone
                        fileAccept=".csv"
                        onFilesAdded={onFileAdded}
                        dropzoneLabel="Modules.PopDisplays.LogFileImport.dropzoneText"
                    />
                    {requestState === NetworkRequestState.Idle && (
                        <Stack gap={1}>
                            {files &&
                                files.map((f, index) => (
                                    <Stack key={f.name} alignItems="center">
                                        <Alert
                                            severity="info"
                                            onClose={() => {
                                                setFiles(prevFiles =>
                                                    prevFiles.filter((_, i) => i !== index),
                                                );
                                            }}
                                        >
                                            {f.name}
                                        </Alert>
                                    </Stack>
                                ))}
                        </Stack>
                    )}
                    {requestState !== NetworkRequestState.Idle && (
                        <Stack direction="row" gap={1} alignItems="center">
                            <Box flex={1}>
                                <LinearProgress
                                    variant="determinate"
                                    value={progressPercentage}
                                    color={progressColor}
                                />
                            </Box>
                            {requestState === NetworkRequestState.InProgress && (
                                <Typography variant="caption">
                                    {progressPercentage.toFixed(0)} %
                                </Typography>
                            )}
                            {requestState === NetworkRequestState.Error && (
                                <Tooltip title={String(error)}>
                                    <Typography color="error.main">
                                        <Error />
                                    </Typography>
                                </Tooltip>
                            )}
                            {requestState === NetworkRequestState.Success && (
                                <Typography color="success.main">
                                    <CheckCircle />
                                </Typography>
                            )}
                        </Stack>
                    )}
                    <Button onClick={uploadFiles} variant="contained">
                        {t('Modules.PopDisplays.LogFileImport.submit')}
                    </Button>
                    {error && (
                        <Alert severity="error" variant="outlined" onClose={reset}>
                            {String(error)}
                        </Alert>
                    )}
                    {data?.data?.success && (
                        <Alert severity="success" variant="outlined" onClose={reset}>
                            {t('Modules.PopDisplays.LogFileImport.uploadSuccess')}
                        </Alert>
                    )}
                </DialogV2>
            </>
        ),
        [
            dropdownValue,
            error,
            files,
            onFileAdded,
            progressColor,
            progressPercentage,
            requestState,
            reset,
            t,
            uploadFiles,
            uploadState,
            closeDialog,
            data,
        ],
    );
    const classicUploadLogHandler = useMemo(
        () => (
            <>
                <Button onClick={classicUploadState.setTrue} variant="contained">
                    {t('Modules.PopDisplays.LogFileImport.uploadClassicLog')}
                </Button>
                <DialogV2
                    onClose={closeClassicDialog}
                    open={classicUploadState.value}
                    maxWidth="xl"
                >
                    <Stack direction="row" justifyContent="center" width="100%">
                        <Button
                            onClick={handleDownloadClassic}
                            variant="contained"
                            sx={{ minWidth: '10rem' }}
                        >
                            {t('Modules.PopDisplays.LogFileImport.downloadClassicTemplate')}
                        </Button>
                    </Stack>
                    <FileUploadDropzone
                        fileAccept=".csv"
                        onFilesAdded={onClassicFileAdded}
                        dropzoneLabel="Modules.PopDisplays.LogFileImport.dropzoneText"
                    />
                    {classicRequestState === NetworkRequestState.Idle && (
                        <Stack gap={1}>
                            {classicFiles &&
                                classicFiles.map((f, index) => (
                                    <Stack key={f.name} alignItems="center">
                                        <Alert
                                            severity="info"
                                            onClose={() => {
                                                setClassicFiles(prevFiles =>
                                                    prevFiles.filter((_, i) => i !== index),
                                                );
                                            }}
                                        >
                                            {f.name}
                                        </Alert>
                                    </Stack>
                                ))}
                        </Stack>
                    )}
                    {classicRequestState !== NetworkRequestState.Idle && (
                        <Stack direction="row" gap={1} alignItems="center">
                            <Box flex={1}>
                                <LinearProgress
                                    variant="determinate"
                                    value={classicProgressPercentage}
                                    color={classicProgressColor}
                                />
                            </Box>
                            {classicRequestState === NetworkRequestState.InProgress && (
                                <Typography variant="caption">
                                    {classicProgressPercentage.toFixed(0)} %
                                </Typography>
                            )}
                            {classicRequestState === NetworkRequestState.Error && (
                                <Tooltip title={String(classicError)}>
                                    <Typography color="error.main">
                                        <Error />
                                    </Typography>
                                </Tooltip>
                            )}
                            {classicRequestState === NetworkRequestState.Success && (
                                <Typography color="success.main">
                                    <CheckCircle />
                                </Typography>
                            )}
                        </Stack>
                    )}
                    <Button
                        onClick={uploadClassicFiles}
                        disabled={classicFiles?.length === 0}
                        variant="contained"
                    >
                        {t('Modules.PopDisplays.LogFileImport.submit')}
                    </Button>
                    {classicError && (
                        <Alert severity="error" variant="outlined" onClose={classicReset}>
                            {String(classicError)}
                        </Alert>
                    )}
                    {classicDate?.data?.success && (
                        <Alert severity="success" variant="outlined" onClose={classicReset}>
                            {t('Modules.PopDisplays.LogFileImport.uploadSuccess')}
                        </Alert>
                    )}
                </DialogV2>
            </>
        ),
        [
            classicDate?.data?.success,
            classicError,
            classicFiles,
            classicProgressColor,
            classicProgressPercentage,
            classicRequestState,
            classicReset,
            classicUploadState.setTrue,
            classicUploadState.value,
            closeClassicDialog,
            onClassicFileAdded,
            t,
            uploadClassicFiles,
        ],
    );
    const handleStatusChange = useCallback(() => {
        statusStart(action as 'reprocess' | 'delete' | 'ignore', ids);
    }, [ids, action, statusStart]);
    const handleActionChange = event => {
        setAction(event.target.value);
    };
    const statusHandler = useMemo(
        () => (
            <Box width="9rem">
                <SelectField
                    value={action}
                    clearAll
                    options={[
                        {
                            label: t('Modules.PopDisplays.LogFileImport.reprocess'),
                            value: 'reprocess',
                        },
                        { label: t('Modules.PopDisplays.LogFileImport.ignore'), value: 'ignore' },
                    ]}
                    label={t('Modules.PopDisplays.LogFileImport.bulkActions')}
                    onChange={handleActionChange}
                    multiple={false}
                />
            </Box>
        ),
        [action, t],
    );
    const statusButton = useMemo(
        () => (
            <>
                {isLoading && <CircularProgress />}
                {!isLoading && (
                    <Stack>
                        <Button
                            onClick={handleStatusChange}
                            disabled={!canApplyBulkAction(ids, action)}
                            variant="contained"
                        >
                            {t('Modules.PopDisplays.LogFileImport.applyAction')}
                        </Button>
                        {statusError && (
                            <Alert severity="error" variant="outlined" onClose={statusReset}>
                                {String(statusError)}
                            </Alert>
                        )}
                    </Stack>
                )}
            </>
        ),
        [action, handleStatusChange, ids, isLoading, statusError, statusReset, t],
    );
    const { keyedEntities } = useEntityData<PopDisplays_LogFileImport>('PopDisplays_LogFileImport');
    const generateRowSx = useCallback(
        (index: number, rowId: number) => (themeArg: Theme) => {
            const row = keyedEntities[rowId];
            if (!row) return {};
            const hasError = row.error !== '';
            if (!hasError) return {};

            const main = themeArg.palette.error.main;
            return {
                backgroundColor: alpha(main, 0.06),
                borderLeft: `4px solid ${main}`,
                '&:hover': { backgroundColor: alpha(main, 0.12) },
            };
        },
        [keyedEntities],
    );
    return (
        <>
            <DialogV2 onClose={() => setErrorPopUp(null)} open={errorPopUp !== null}>
                {errorPopUp}
            </DialogV2>
            <FullPageEntityTableWrapper
                title="Modules.PopDisplays.LogFileImport.metaTitle"
                controls={useMemo(
                    () => [
                        statusHandler,
                        statusButton,
                        rawUploadLogHandler,
                        classicUploadLogHandler,
                    ],
                    [statusHandler, statusButton, rawUploadLogHandler, classicUploadLogHandler],
                )}
            >
                <FilterableEntityTable
                    ref={tableRef}
                    key={paginationTag}
                    tag={paginationTag}
                    columns={columns(setIds, ids, paginatedIds, setErrorPopUp)}
                    baseEntityName="PopDisplays_LogFileImport"
                    textSearchColumns={textSearchCols}
                    rendererType={FilterableEntityTableRendererType.Contained}
                    showFilters
                    generateRowSx={generateRowSx}
                />
            </FullPageEntityTableWrapper>
        </>
    );
};

const setup: PageConfig = {
    id: 'LogFileImportListPage',
    component: LogFileImportListPage,
    disableDesktopContentScrolling: true,
    systemSearch: {
        title: 'Modules.PopDisplays.LogFileImport.metaTitle',
        description: 'Modules.PopDisplays.LogFileImport.description',
        type: 'page',
        icon: logFileImportIcon,
        aliases: 'SystemSearch.aliases.popDisplaysLogFileImportList',
        route: PostAuthRoutes.PopDisplays.LogFileImportList,
        saveAsRecentPage: true,
        breadcrumbs: [
            { label: 'Modules.Admin.Home.metaTitle', link: PostAuthRoutes.Admin.AdminHome },
            {
                label: 'Modules.PopDisplays.AdminHome.metaTitle',
                link: PostAuthRoutes.PopDisplays.PopDisplaysHome,
            },
            { label: 'Modules.PopDisplays.LogFileImport.metaTitle' },
        ],
        permissions: [PermissionName.Pop_displaysLog_file_importsRead],
    },
};

export default setup;
